import React,{useState,useEffect} from 'react';
import { T, alpha } from './poyais-ui/theme';
import { ScreenHeader } from './poyais-ui/shell';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Müşteri Yöneticisi': 'Client Admin', 'Editör': 'Editor', 'Görüntüleyici': 'Viewer',
  'Takım bilgisi alınamadı': 'Could not load team info',
  'Geçerli bir e-posta girin': 'Enter a valid email address',
  'Davet gönderilemedi': 'Could not send invite',
  'Davet gönderildi': 'Invite sent',
  'Bu üyeyi takımdan çıkarmak istediğinize emin misiniz?': 'Are you sure you want to remove this member from the team?',
  'Üye çıkarıldı': 'Member removed',
  'İşlem başarısız': 'Action failed',
  'Rol güncellenemedi': 'Could not update role',
  'Rol güncellendi': 'Role updated',
  'Davet iptal edildi': 'Invite canceled',
  'Yükleniyor...': 'Loading...',
  'Takım': 'Team',
  'üye': 'members',
  'Yeni Üye Davet Et': 'Invite New Member',
  'Planınızın takım üyesi limitine ulaştınız. Daha fazla üye davet etmek için planınızı yükseltin.': 'You have reached your plan\'s team member limit. Upgrade your plan to invite more members.',
  'E-posta': 'Email',
  'Rol': 'Role',
  'Gönderiliyor...': 'Sending...',
  'Davet Gönder': 'Send Invite',
  'Üyeler': 'Members',
  'Çıkar': 'Remove',
  'Bekleyen Davetler': 'Pending Invites',
  'tarihine kadar geçerli': 'valid until',
  'İptal Et': 'Cancel',
  'Takım üyelerinizi yönetin.': 'Manage your team members.',
});

const ROLE_LABEL: Record<string,string> = { admin: 'Müşteri Yöneticisi', editor: 'Editör', viewer: 'Görüntüleyici' };

const TakimPage:React.FC<{config?:any}>=()=>{
  const { t, lang } = useLang();
  const [loading,setLoading]=useState(true);
  const [members,setMembers]=useState<any[]>([]);
  const [pendingInvites,setPendingInvites]=useState<any[]>([]);
  const [maxTeamMembers,setMaxTeamMembers]=useState<number|null>(null);
  const [inviteEmail,setInviteEmail]=useState('');
  const [inviteRole,setInviteRole]=useState('viewer');
  const [inviteLoading,setInviteLoading]=useState(false);
  const [toast,setToast]=useState('');
  const [actionLoading,setActionLoading]=useState('');

  const token=()=>localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
  const headers={'Content-Type':'application/json','Authorization':'Bearer '+token()};
  const clientId=()=>parseInt(localStorage.getItem('poyais_active_client')||'1');

  const showToast=(m:string)=>{ setToast(m); setTimeout(()=>setToast(''),3000); };

  const loadTeam=()=>{
    setLoading(true);
    fetch(`/api/v1/clients/${clientId()}/team`,{headers})
      .then(r=>r.json())
      .then(d=>{
        setMembers(d.members||[]);
        setPendingInvites(d.pending_invites||[]);
      })
      .catch(()=>showToast(t('Takım bilgisi alınamadı')))
      .finally(()=>setLoading(false));
  };

  const loadPlanLimit=()=>{
    fetch('/api/v1/subscription/plans',{headers})
      .then(r=>r.json())
      .then(d=>{
        fetch('/api/v1/subscription/status',{headers})
          .then(r=>r.json())
          .then(s=>{
            const plan = d.plans?.[s.plan];
            if(plan) setMaxTeamMembers(plan.limits.max_team_members);
          });
      })
      .catch(()=>{});
  };

  useEffect(()=>{ loadTeam(); loadPlanLimit(); },[]);

  const handleInvite=()=>{
    if(!inviteEmail.includes('@')){ showToast(t('Geçerli bir e-posta girin')); return; }
    setInviteLoading(true);
    fetch(`/api/v1/clients/${clientId()}/invite`,{
      method:'POST', headers,
      body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
    }).then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(!ok){ showToast(d.detail||t('Davet gönderilemedi')); return; }
        showToast(t('Davet gönderildi'));
        setInviteEmail('');
        loadTeam();
      })
      .catch(()=>showToast(t('Davet gönderilemedi')))
      .finally(()=>setInviteLoading(false));
  };

  const handleRemoveMember=(uid:number)=>{
    if(!window.confirm(t('Bu üyeyi takımdan çıkarmak istediğinize emin misiniz?'))) return;
    setActionLoading('remove-'+uid);
    fetch(`/api/v1/clients/${clientId()}/users/${uid}`,{method:'DELETE',headers})
      .then(()=>{ showToast(t('Üye çıkarıldı')); loadTeam(); })
      .catch(()=>showToast(t('İşlem başarısız')))
      .finally(()=>setActionLoading(''));
  };

  const handleRoleChange=(uid:number,role:string)=>{
    setActionLoading('role-'+uid);
    fetch(`/api/v1/clients/${clientId()}/users/${uid}/role`,{method:'PATCH',headers,body:JSON.stringify({role})})
      .then(r=>r.json().then(d=>({ok:r.ok,d})))
      .then(({ok,d})=>{
        if(!ok){ showToast(d.detail||t('Rol güncellenemedi')); return; }
        showToast(t('Rol güncellendi'));
        loadTeam();
      })
      .catch(()=>showToast(t('Rol güncellenemedi')))
      .finally(()=>setActionLoading(''));
  };

  const handleCancelInvite=(inviteId:number)=>{
    setActionLoading('cancel-'+inviteId);
    fetch(`/api/v1/clients/${clientId()}/invites/${inviteId}`,{method:'DELETE',headers})
      .then(()=>{ showToast(t('Davet iptal edildi')); loadTeam(); })
      .catch(()=>showToast(t('İşlem başarısız')))
      .finally(()=>setActionLoading(''));
  };

  if(loading) return <PoyaisSpinner size={36} pad={24}/>;

  const totalCount = members.length;
  const limitReached = maxTeamMembers !== null && totalCount >= maxTeamMembers;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {toast && (
        <div style={{position:'fixed',top:20,right:20,background:'var(--py-ink)',color:'#fff',padding:'10px 18px',borderRadius:10,fontSize:13,zIndex:9999}}>{toast}</div>
      )}
      <ScreenHeader title={t('Takım')} subtitle={`${t('Takım üyelerinizi yönetin.')}${maxTeamMembers !== null ? ` (${totalCount}/${maxTeamMembers} ${t('üye')})` : ''}`} />

      {/* Davet formu */}
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,padding:'20px 22px'}}>
        <div style={{fontSize:13.5,fontWeight:800,marginBottom:14}}>{t('Yeni Üye Davet Et')}</div>
        {limitReached ? (
          <div style={{fontSize:12.5,color:T.redText}}>{t('Planınızın takım üyesi limitine ulaştınız. Daha fazla üye davet etmek için planınızı yükseltin.')}</div>
        ) : (
          <div style={{display:'flex',gap:10,flexWrap:'wrap' as const,alignItems:'flex-end'}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:11.5,color:'var(--py-text-mid-alt)',marginBottom:5}}>{t('E-posta')}</div>
              <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="ornek@sirket.com"
                style={{width:'100%',padding:'9px 13px',borderRadius:9,border:`1px solid ${T.borderMid}`,fontSize:13,outline:'none',boxSizing:'border-box' as const}}/>
            </div>
            <div>
              <div style={{fontSize:11.5,color:'var(--py-text-mid-alt)',marginBottom:5}}>{t('Rol')}</div>
              <select value={inviteRole} onChange={e=>setInviteRole(e.target.value)}
                style={{padding:'9px 13px',borderRadius:9,border:`1px solid ${T.borderMid}`,fontSize:13,outline:'none',background:T.surface}}>
                <option value="viewer">{t('Görüntüleyici')}</option>
                <option value="editor">{t('Editör')}</option>
                <option value="admin">{t('Müşteri Yöneticisi')}</option>
              </select>
            </div>
            <button onClick={handleInvite} disabled={inviteLoading}
              style={{padding:'9px 16px',borderRadius:11,border:'none',background:T.accent,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
              {inviteLoading?t('Gönderiliyor...'):t('Davet Gönder')}
            </button>
          </div>
        )}
      </div>

      {/* Mevcut üyeler */}
      <div>
        <div style={{fontSize:13.5,fontWeight:800,marginBottom:12}}>{t('Üyeler')}</div>
        <div style={{display:'flex',flexDirection:'column' as const,gap:8}}>
          {members.map(m=>(
            <div key={m.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'13px 18px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{m.name} {m.surname}</div>
                <div style={{fontSize:11.5,color:T.textDim}}>{m.email}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <select value={m.role} disabled={actionLoading==='role-'+m.id} onChange={e=>handleRoleChange(m.id,e.target.value)}
                  style={{fontSize:11,fontWeight:700,color:'var(--py-text-mid-alt)',background:'var(--py-track-bg)',padding:'5px 10px',borderRadius:999,border:'none',cursor:'pointer'}}>
                  <option value="viewer">{t('Görüntüleyici')}</option>
                  <option value="editor">{t('Editör')}</option>
                  <option value="admin">{t('Müşteri Yöneticisi')}</option>
                </select>
                <button onClick={()=>handleRemoveMember(m.id)} disabled={actionLoading==='remove-'+m.id}
                  style={{padding:'6px 12px',borderRadius:8,border:`1px solid ${alpha(T.red, 25)}`,background:'var(--py-red-tint)',color:T.redText,fontSize:11.5,fontWeight:600,cursor:'pointer'}}>
                  {t('Çıkar')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bekleyen davetler */}
      {pendingInvites.length > 0 && (
        <div>
          <div style={{fontSize:13.5,fontWeight:800,marginBottom:12}}>{t('Bekleyen Davetler')}</div>
          <div style={{display:'flex',flexDirection:'column' as const,gap:8}}>
            {pendingInvites.map(inv=>(
              <div key={inv.id} style={{background:'var(--py-subtle-bg)',border:`1px solid ${T.border}`,borderRadius:14,padding:'13px 18px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontSize:13,color:T.text}}>{inv.email}</div>
                  <div style={{fontSize:11.5,color:T.textDim}}>{t(ROLE_LABEL[inv.role]||inv.role)} · {lang==='en' ? `${t('tarihine kadar geçerli')} ${new Date(inv.expires_at).toLocaleDateString('en-US')}` : `${new Date(inv.expires_at).toLocaleDateString('tr-TR')} ${t('tarihine kadar geçerli')}`}</div>
                </div>
                <button onClick={()=>handleCancelInvite(inv.id)} disabled={actionLoading==='cancel-'+inv.id}
                  style={{padding:'6px 12px',borderRadius:8,border:`1px solid ${T.borderMid}`,background:T.surface,color:'var(--py-text-mid-alt)',fontSize:11.5,fontWeight:600,cursor:'pointer'}}>
                  {t('İptal Et')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default TakimPage;
